/**
 * DockMate Socket Service
 * Handles real-time WebSocket connections
 */

import { io } from 'socket.io-client';
import { authAPI } from './api';

const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || 'http://localhost:3001';

let socket = null;
let eventHandlers = {};

/**
 * Connect to the WebSocket server
 */
export function connect() {
  const token = authAPI.getToken();

  if (!token) {
    console.warn('No auth token available for socket connection');
    return null;
  }

  if (socket?.connected) {
    return socket;
  }

  socket = io(SOCKET_URL, {
    auth: { token },
    transports: ['websocket', 'polling'],
    reconnection: true,
    reconnectionAttempts: 5,
    reconnectionDelay: 1000,
  });

  socket.on('connect', () => {
    console.log('⚓ Socket connected');
  });

  socket.on('disconnect', (reason) => {
    console.log('Socket disconnected:', reason);
  });

  socket.on('connect_error', (error) => {
    console.error('Socket connection error:', error.message);
  });

  // Re-attach any registered event handlers
  Object.entries(eventHandlers).forEach(([event, handlers]) => {
    handlers.forEach(handler => {
      socket.on(event, handler);
    });
  });

  return socket;
}

/**
 * Disconnect from the WebSocket server
 */
export function disconnect() {
  if (socket) {
    socket.disconnect();
    socket = null;
  }
  eventHandlers = {};
}

/**
 * Get the socket instance
 */
export function getSocket() {
  return socket;
}

/**
 * Register an event handler
 */
export function on(event, handler) {
  if (!eventHandlers[event]) {
    eventHandlers[event] = [];
  }
  eventHandlers[event].push(handler);

  if (socket) {
    socket.on(event, handler);
  }

  // Return unsubscribe function
  return () => {
    off(event, handler);
  };
}

/**
 * Remove an event handler
 */
export function off(event, handler) {
  if (eventHandlers[event]) {
    eventHandlers[event] = eventHandlers[event].filter(h => h !== handler);
  }

  if (socket) {
    socket.off(event, handler);
  }
}

/**
 * Emit an event
 */
export function emit(event, data) {
  if (socket?.connected) {
    socket.emit(event, data);
  } else {
    console.warn('Socket not connected. Cannot emit:', event);
  }
}

// ======================
// Chat Events
// ======================

export function joinConversation(conversationId) {
  emit('join_conversation', conversationId);
}

export function leaveConversation(conversationId) {
  emit('leave_conversation', conversationId);
}

export function startTyping(conversationId) {
  emit('typing_start', { conversation_id: conversationId });
}

export function stopTyping(conversationId) {
  emit('typing_stop', { conversation_id: conversationId });
}

export function markMessagesRead(conversationId, messageIds) {
  emit('mark_read', { conversation_id: conversationId, message_ids: messageIds });
}

// ======================
// Helper Events
// ======================

export function toggleAvailability(available) {
  emit('toggle_availability', { available });
}

export function updateLocation(serviceId, lat, lng) {
  emit('location_update', { service_id: serviceId, lat, lng });
}

// ======================
// Event Constants
// ======================

export const EVENTS = {
  // Chat events
  NEW_MESSAGE: 'new_message',
  USER_TYPING: 'user_typing',
  USER_STOPPED_TYPING: 'user_stopped_typing',
  MESSAGES_READ: 'messages_read',

  // Request events
  NEW_REQUEST: 'new_request',
  REQUEST_UPDATED: 'request_updated',
  NEW_APPLICATION: 'new_application',
  HELPER_SELECTED: 'helper_selected',

  // Service events
  SERVICE_STARTED: 'service_started',
  SERVICE_COMPLETED: 'service_completed',
  HELPER_LOCATION: 'helper_location',

  // Availability events
  AVAILABILITY_UPDATED: 'availability_updated',

  // General notifications
  NOTIFICATION: 'notification',
};

export default {
  connect,
  disconnect,
  getSocket,
  on,
  off,
  emit,
  joinConversation,
  leaveConversation,
  startTyping,
  stopTyping,
  markMessagesRead,
  toggleAvailability,
  updateLocation,
  EVENTS,
};
